import { useContext, useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Graph from '../components/graph';
import DashboardCard from '../components/dashboard-card';
import { GlobalContext, handleError } from '../context/Provider';
import henceforthApi from '../utils/henceforthApi';
import { numberWithCommas } from '../utils/validations';
import RecentOrdersTable from '../components/common/RecentOrdersTable';
import { ColDef } from 'ag-grid-community';
import { ArrowRight, Package } from 'lucide-react';

const Sales = 'sales';
const Product = 'product';

interface Counts {
  total_products: string;
  total_orders: string;
  alert_of_stock: string;
  out_of_stock: string;
  total_earnings: string;
  total_ratings: string;
  total_reviews: string;
}

interface DashboardCardItem {
  key: string;
  url: string;
  title: string;
  count: string | number;
  description: string;
  icon: string;
  badge?: string;
  badgeType: 'success' | 'info' | 'warning' | 'neutral';
}

const Home = () => {
  const { authState, language_data, authDispatch } = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const newParam = new URLSearchParams(location.search);
  const ProductMatch = newParam.get(Product) as string | null;
  const SalesMatch = newParam.get(Sales) as string | null;

  const [count, setCount] = useState<Counts>({
    total_products: '0',
    total_orders: '0',
    alert_of_stock: '0',
    out_of_stock: '0',
    total_earnings: '0',
    total_ratings: '0',
    total_reviews: '0',
  });

  // Local loading state (global loading se flicker nahi hoga)
  const [loadingCounts, setLoadingCounts] = useState<boolean>(true);

  const [loadingG, setLoadingG] = useState({
    product: false,
    sales: false,
  });

  const [productX, setProductX] = useState<string>('hour');
  const [salesX, setSalesX] = useState<string>('hour');
  const [productGraph, setProductGraph] = useState([] as any);
  const [salesGraph, setSalesGraph] = useState([] as any);

  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [recentProducts, setRecentProducts] = useState<any[]>([]);
  const [loadingRecent, setLoadingRecent] = useState<boolean>(true);

  const dashboardDetails = async () => {
    setLoadingCounts(true);
    try {
      const res = (await henceforthApi.Dashboard.getTotalOrderCount()).data;
      if (res) {
        setCount({
          total_products: String(res.total_products ?? 0),
          total_orders: String(res.total_orders ?? 0),
          alert_of_stock: String(res.alert_of_stock ?? 0),
          out_of_stock: String(res.out_of_stock ?? 0),
          total_earnings: String(res.total_earnings ?? 0),
          total_ratings: String(res.total_ratings ?? 0),
          total_reviews: String(res.total_reviews ?? 0),
        });
      }
    } catch (error: any) {
      handleError(error, '', authDispatch);
    } finally {
      setLoadingCounts(false);
    }
  };

  const fetchRecentData = async () => {
    setLoadingRecent(true);
    try {
      const orderRes = (await henceforthApi.Order.getOrderList(0, 5, '')).data;
      if (orderRes?.data && Array.isArray(orderRes.data)) {
        setRecentOrders(orderRes.data);
      } else {
        setRecentOrders([]);
      }
    } catch (e) {
      setRecentOrders([]);
    }

    try {
      const productRes = (await henceforthApi.Product.getProductList('', 0, 5, '')).data;
      if (productRes?.data && Array.isArray(productRes.data)) {
        setRecentProducts(productRes.data);
      } else {
        setRecentProducts([]);
      }
    } catch (e) {
      setRecentProducts([]);
    } finally {
      setLoadingRecent(false);
    }
  };

  const dashboadGraph = async (graph: string, type: string) => {
    if (graph && type) {
      newParam.set(graph, type);
    }
    const types =
      type === 'DAILY'
        ? 'hour'
        : type === 'WEEKLY'
          ? 'day'
          : type === 'MONTHLY'
            ? 'date'
            : type === 'YEARLY'
              ? 'month'
              : 'hour';

    if (graph === Product) setProductX(types);
    if (graph === Sales) setSalesX(types);

    setLoadingG((prev) => ({
      ...prev,
      [graph]: true,
    }));

    try {
      const res = (await henceforthApi.Dashboard.getGraph(graph, type)).data;
      const graphData = res?.data?.data || res?.data || [];
      if (graph === 'product') {
        setProductGraph(Array.isArray(graphData) ? graphData : []);
      } else {
        setSalesGraph(Array.isArray(graphData) ? graphData : []);
      }
      navigate({ search: newParam.toString() });
    } catch (error: any) {
      handleError(error, '', authDispatch);
    } finally {
      setLoadingG((prev) => ({
        ...prev,
        [graph]: false,
      }));
    }
  };

  useEffect(() => {
    dashboardDetails();
    fetchRecentData();
  }, []);

  useEffect(() => {
    dashboadGraph('product', 'DAILY');
    dashboadGraph('sales', 'DAILY');
  }, []);

  // Calculated values
  const earningsFormatted = count.total_earnings
    ? `$${numberWithCommas(Number(count.total_earnings))}`
    : '$0.00';
  const ordersCount = Number(count.total_orders) || 0;
  const productsCount = Number(count.total_products) || 0;
  const alertStockCount = Number(count.alert_of_stock) || 0;
  const outOfStockCount = Number(count.out_of_stock) || 0;
  const ratingScore = Number(count.total_ratings) || 0;
  const reviewsCount = Number(count.total_reviews) || 0;

  const t = language_data?.[authState.lang];

  // ✅ Single variable: saare cards ka data yahan hai
  const dashboardCards: DashboardCardItem[] = useMemo(
    () => [
      {
        key: 'earnings',
        url: '/earnings/1',
        title: t?.common_earning || 'Total Earnings',
        count: earningsFormatted,
        description:
          t?.home_addition_of_all_order_pricing || 'Completed orders total',
        icon: 'fa-money',
        badgeType: 'info',
      },
      {
        key: 'orders',
        url: '/orders/1',
        title: t?.common_order || 'Total Orders',
        count: ordersCount,
        description: `${ordersCount} total orders recorded`,
        icon: 'fa-shopping-bag',
        badge: ordersCount > 0 ? `${ordersCount} Booked` : undefined,
        badgeType: ordersCount > 0 ? 'success' : 'neutral',
      },
      {
        key: 'products',
        url: '/products/1',
        title: t?.common_product || 'Active Catalog',
        count: productsCount,
        description: `${productsCount} live store items`,
        icon: 'fa-cubes',
        badge: productsCount > 0 ? `${productsCount} Listed` : undefined,
        badgeType: productsCount > 0 ? 'info' : 'neutral',
      },
      {
        key: 'ratings',
        url: '/ratings/1?order_status=DELIVERED',
        title: t?.common_rating_reviews || 'Ratings & Reviews',
        count: reviewsCount > 0 ? `${ratingScore} ★` : '0',
        description: `${reviewsCount} total customer reviews`,
        icon: 'fa-star',
        badge: reviewsCount > 0 ? `${reviewsCount} Reviews` : undefined,
        badgeType: 'neutral',
      },
      {
        key: 'stock_alert',
        url: '/products/1?qty=5',
        title: t?.common_alert_of_stock || 'Stock Alert',
        count: alertStockCount,
        description: 'Items with quantity < 5',
        icon: 'fa-bell',
        badge: alertStockCount === 0 ? 'Optimal Levels' : `${alertStockCount} Low`,
        badgeType: alertStockCount === 0 ? 'success' : 'warning',
      },
      {
        key: 'out_of_stock',
        url: '/products/1?out_of_stock=true',
        title: t?.common_out_of_stock || 'Out of Stock',
        count: outOfStockCount,
        description: 'Depleted store items',
        icon: 'fa-shopping-cart',
        badge: outOfStockCount === 0 ? 'All In Stock' : `${outOfStockCount} Out`,
        badgeType: outOfStockCount === 0 ? 'success' : 'warning',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, language_data, authState.lang]
  );

  const orderColumns = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'ORDER ID',
        field: 'order_id',
        minWidth: 140,
        flex: 1.2,
        cellRenderer: (params: any) => {
          const id =
            params.data?.order_id ||
            String(params.data?._id || '').slice(-6).toUpperCase();
          return (
            <Link
              to={`/order/${params.data?._id}`}
              className="font-bold text-[#1AA7DE] hover:underline flex items-center h-full"
            >
              #{id}
            </Link>
          );
        },
      },
      {
        headerName: 'CUSTOMER',
        field: 'user_name',
        minWidth: 180,
        flex: 1.5,
        cellRenderer: (params: any) => {
          const name =
            params.data?.user_name || params.data?.customer || 'Customer';
          const initials = name.substring(0, 2).toUpperCase();
          return (
            <div className="flex items-center gap-2.5 h-full">
              <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center shrink-0">
                {initials}
              </span>
              <span className="font-semibold text-slate-800 truncate">
                {name}
              </span>
            </div>
          );
        },
      },
      {
        headerName: 'STATUS',
        field: 'order_status',
        minWidth: 140,
        flex: 1.2,
        cellRenderer: (params: any) => {
          const status =
            params.data?.order_status || params.data?.status || 'Active';
          const isDelivered = status === 'DELIVERED';
          return (
            <div className="flex items-center h-full">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${isDelivered
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-sky-50 text-sky-700 border border-sky-200'
                  }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${isDelivered ? 'bg-emerald-500' : 'bg-[#1AA7DE]'
                    }`}
                />
                {status}
              </span>
            </div>
          );
        },
      },
      {
        headerName: 'ITEMS',
        field: 'items_count',
        minWidth: 110,
        flex: 1,
        cellRenderer: (params: any) => {
          const itemsCount =
            params.data?.order_items?.length || params.data?.items_count || 1;
          return (
            <div className="flex items-center h-full text-slate-600 font-medium text-sm">
              {itemsCount} {itemsCount === 1 ? 'Item' : 'Items'}
            </div>
          );
        },
      },
      {
        headerName: 'TOTAL AMOUNT',
        field: 'net_amount',
        minWidth: 130,
        flex: 1.2,
        cellRenderer: (params: any) => {
          const amount = params.data?.net_amount || params.data?.amount || 0;
          return (
            <div className="flex items-center justify-end h-full font-black text-slate-900 text-base">
              ${numberWithCommas(amount)}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-slate-50/50 min-h-screen">
      {/* Top Welcome Bar */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-white p-6 sm:p-7 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <h4 className="text-xl lg:text-2xl font-black text-slate-900 tracking-tight capitalize m-0">
            Welcome back, {authState.name || 'Seller'}
          </h4>
        </div>
      </div>

      {/* Metric Cards (single array + map) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
        {dashboardCards.map((card) => (
          <DashboardCard
            key={card.key}
            url={card.url}
            title={card.title}
            count={card.count}
            description={card.description}
            icon={card.icon}
            loading={loadingCounts}
            badge={card.badge}
            badgeType={card.badgeType}
          />
        ))}
      </div>

      {/* 2 Graphs Row */}
      <div className="flex flex-wrap -mx-2">
        {/* Product Graph */}
        <Graph
          heading={language_data?.[authState.lang]?.common_product_graph || 'Product Graph'}
          Match={ProductMatch}
          run={Product}
          data={productGraph}
          xAxis={productX}
          yAxis={language_data?.[authState.lang]?.home_total_product || 'total_products'}
          dashboadGraph={dashboadGraph}
          loading={loadingG.product}
          badgeText={`${productsCount} Products`}
          subtitle="Catalog growth over time"
          legendText="Total Products"
        />

        {/* Sales Graph */}
        <Graph
          heading={language_data?.[authState.lang]?.common_sales_graph || 'Sales graph'}
          Match={SalesMatch}
          run={Sales}
          data={salesGraph}
          xAxis={salesX}
          yAxis={language_data?.[authState.lang]?.home_total_orders || 'total_orders'}
          dashboadGraph={dashboadGraph}
          loading={loadingG.sales}
          badgeText={`${ordersCount} Orders`}
          subtitle="Order fulfillment velocity & metrics"
          legendText="Total Orders"
        />
      </div>

      {/* Bottom Row: Catalog Overview & Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Catalog Overview (5 cols) */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900 m-0">Catalog Overview</h3>
              </div>
              <Link
                to="/products/1"
                className="text-base font-bold text-[#1AA7DE] hover:text-[#148bb9] inline-flex items-center gap-1.5 group"
              >
                Manage{' '}
                <ArrowRight className="w-4.5 h-4.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            {recentProducts.length > 0 ? (
              <div className="space-y-4">
                {recentProducts.map((item: any, idx: number) => (
                  <div
                    key={item._id || idx}
                    className="flex items-center justify-between p-2 rounded-xl border border-slate-100 hover:border-sky-200 transition-colors bg-slate-50/50 mt-4"
                  >
                    <div className="flex items-center gap-2">
                      {item.images?.[0] || item.image ? (
                        <img
                          src={`${henceforthApi.API_FILE_ROOT_SMALL}${item.images?.[0] || item.image}`}
                          alt={item.name}
                          className="w-12 h-12 rounded-xl object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center font-bold">
                          <Package className="w-5 h-5" />
                        </div>
                      )}
                      <div>
                        <h4 className="text-base sm:text-lg font-bold text-slate-900 m-0 line-clamp-1">
                          {item.name || item.title || 'Product'}
                        </h4>
                        <p className="text-sm sm:text-base text-slate-500 m-0 mt-0.5">
                          {item.sku ? `SKU: ${item.sku}` : item.category?.name || 'In Catalog'}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-lg sm:text-xl font-black text-[#1AA7DE] block">
                        ${item.price || item.selling_price || 0}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200/50">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        {item.quantity ?? 0} In Stock
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center rounded-xl bg-slate-50/70 border border-dashed border-slate-200">
                <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-base text-slate-700 font-semibold m-0">No products added yet.</p>
                <Link
                  to="/product/add"
                  className="mt-2 text-base font-bold text-[#1AA7DE] hover:underline inline-block"
                >
                  + Add your first product
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Right: Recent Orders (7 cols) */}
        <div className="lg:col-span-7">
          <RecentOrdersTable
            orders={recentOrders}
            loading={loadingRecent}
            totalCount={ordersCount}
            pageSize={5}
            title="Recent Orders Activity"
            viewAllLink={ordersCount > 0 ? '/orders/1' : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default Home;